import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Photo } from '../types/Photo';
import { getAllPhotos } from '../services/photoService';
import PhotoCard from './PhotoCard';
import PhotoDetail from './PhotoDetail';

const GalleryContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 40px 20px;
`;

const Title = styled.h1`
  font-size: 48px;
  font-weight: 700;
  margin-bottom: 40px;
  text-align: center;
  color: #333;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 24px;
  margin-bottom: 40px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #666;
`;

const ErrorContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
  font-size: 18px;
  color: #d32f2f;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: #666;

  h2 {
    font-size: 24px;
    margin-bottom: 12px;
  }

  p {
    font-size: 16px;
    color: #999;
  }
`;

const PhotoGallery: React.FC = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        setLoading(true);
        const fetchedPhotos = await getAllPhotos();
        setPhotos(fetchedPhotos);
        setError(null);
      } catch (err) {
        setError('Failed to load photos. Please try again later.');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchPhotos();
  }, []);

  const handlePhotoClick = (photo: Photo) => {
    setSelectedPhoto(photo);
  };

  const handleCloseDetail = () => {
    setSelectedPhoto(null);
  };

  if (loading) {
    return (
      <GalleryContainer>
        <LoadingContainer>Loading photos...</LoadingContainer>
      </GalleryContainer>
    );
  }

  if (error) {
    return (
      <GalleryContainer>
        <ErrorContainer>{error}</ErrorContainer>
      </GalleryContainer>
    );
  }

  if (photos.length === 0) {
    return (
      <GalleryContainer>
        <EmptyState>
          <h2>No Photos Yet</h2>
          <p>Start adding photos to your portfolio.</p>
        </EmptyState>
      </GalleryContainer>
    );
  }

  return (
    <GalleryContainer>
      <Title>My Photography Portfolio</Title>
      <Grid>
        {photos.map((photo) => (
          <PhotoCard
            key={photo.id}
            photo={photo}
            onClick={() => handlePhotoClick(photo)}
          />
        ))}
      </Grid>
      <PhotoDetail photo={selectedPhoto} onClose={handleCloseDetail} />
    </GalleryContainer>
  );
};

export default PhotoGallery;
